// Firebase imports
import { 
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toDRXError } from '@/lib/drx-error';

// Admin Settings Types
export interface AdminSettings {
  id?: string;
  siteName: string;
  siteDescription: string;
  siteUrl: string;
  emailFrom: string;
  registrationEnabled: boolean;
  emailVerificationRequired: boolean;
  maintenanceMode: boolean;
  currency: string;
  timezone: string;
  maxFileSize: number; // in MB
  sessionTimeout: number; // in minutes
  maxLoginAttempts: number;
  createdAt?: any;
  updatedAt?: any;
}

export interface IAdminSettingsRepository {
  getSettings(): Promise<AdminSettings>;
  updateSettings(settings: Partial<AdminSettings>): Promise<void>;
}

/**
 * Admin Settings Repository
 * Handles database operations for admin settings
 */
export class AdminSettingsRepository implements IAdminSettingsRepository {
  private collectionName = 'adminSettings';
  private settingsDocId = 'global'; // Single document for global settings

  constructor() {
    console.log('[AdminSettingsRepository] Constructor - Initialized');
  }

  private toRepositoryError(
    error: unknown,
    code: string,
    devMessage: string,
    context?: Record<string, unknown>,
  ) {
    return toDRXError(error, {
      code,
      devMessage,
      context,
    });
  }

  /**
   * Get admin settings from database
   * Returns default settings if none exist
   */
  async getSettings(): Promise<AdminSettings> {
    console.log('[AdminSettingsRepository.getSettings] START');
    try {
      const settingsRef = doc(db, this.collectionName, this.settingsDocId);
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const settings = {
          id: settingsSnap.id,
          ...settingsSnap.data(),
        } as AdminSettings;
        console.log('[AdminSettingsRepository.getSettings] SUCCESS - Found settings');
        return settings;
      } else {
        // Return default settings if none exist
        const defaultSettings = this.getDefaultSettings();
        console.log('[AdminSettingsRepository.getSettings] SUCCESS - Using default settings');
        return defaultSettings;
      }
    } catch (error) {
      console.error('[AdminSettingsRepository.getSettings] ERROR:', error);
      // Return default settings on error
      return this.getDefaultSettings();
    }
  }

  /**
   * Update admin settings in database
   */
  async updateSettings(settingsUpdate: Partial<AdminSettings>): Promise<void> {
    console.log('[AdminSettingsRepository.updateSettings] START - Update:', settingsUpdate);
    try {
      const settingsRef = doc(db, this.collectionName, this.settingsDocId);
      
      // Get current settings first to merge
      const currentSettings = await this.getSettings();
      
      // Merge with updates
      const updatedSettings = {
        ...currentSettings,
        ...settingsUpdate,
        updatedAt: serverTimestamp(),
      };

      // If this is the first time saving, add createdAt
      if (!currentSettings.createdAt) {
        updatedSettings.createdAt = serverTimestamp();
      }

      await setDoc(settingsRef, updatedSettings, { merge: true });
      console.log('[AdminSettingsRepository.updateSettings] SUCCESS - Settings updated');
    } catch (error) {
      console.error('[AdminSettingsRepository.updateSettings] ERROR:', error);
      throw this.toRepositoryError(error, 'ADMIN_SETTINGS_UPDATE_FAILED', 'Failed to update admin settings', {
        settingsDocId: this.settingsDocId,
        settingsUpdate,
      });
    }
  }

  /**
   * Get default settings
   */
  private getDefaultSettings(): AdminSettings {
    return {
      siteName: "Dance Realm X",
      siteDescription: "Professional dance education platform",
      siteUrl: "https://dancerealmx.com",
      emailFrom: "noreply@dancerealmx.com",
      registrationEnabled: true,
      emailVerificationRequired: true,
      maintenanceMode: false,
      currency: "USD",
      timezone: "America/New_York",
      maxFileSize: 10, // 10MB
      sessionTimeout: 30, // 30 minutes
      maxLoginAttempts: 5,
    };
  }
}
